/**
 * @module tiptap-extensions
 *
 * Shared TipTap extension registry for the textbox editor and view-mode
 * renderer (PRD-RTE-TIPTAP-document-model §TR4).
 *
 * As of the RTE unification (2026-09), this list is built on top of
 * `@o2d/rich-text-editor`'s `createRichTextExtensions()` factory instead of a
 * hand-maintained, independent copy — StarterKit, TextStyle/Color/FontFamily,
 * the custom `FontSize` mark, TaskList/TaskItem and TextAlign now come from
 * there, configured for the textbox's own needs (undo owned by
 * `@o2d/history`, no trailing-node artifact, no tables, no real
 * Superscript/Subscript/Highlight/Image — see the options passed below and
 * `RichTextExtensionOptions`'s own doc comments for why each is off). Only
 * what has no equivalent in the shared package — `DeferredFormatsExt` — stays
 * defined here, passed in via the factory's `extras` option.
 *
 * `Underline` is NOT imported separately here (it was, until this
 * unification, alongside a plain `StarterKit` registration that already
 * bundles it — the two silently fought over the same extension name).
 * StarterKit 3.27.x bundles `Underline` by default; the factory's
 * `StarterKit.configure(...)` call never disables it.
 *
 * ## Undo/redo: StarterKit history disabled
 *
 * `undoRedo: false` is passed to StarterKit because `@o2d/history` manages
 * undo at the board level. Having TipTap also stack edits would double-record
 * every keystroke.
 */

import { Extension } from '@tiptap/core';
import { createRichTextExtensions } from '@o2d/rich-text-editor';
// Side-effect-only: `@tiptap/extension-text-align`'s own `declare module
// '@tiptap/core'` augmentation (adds `setTextAlign` etc. to `ChainedCommands`)
// only lands in THIS package's type-check program if something here actually
// references the module — the extension instance itself now comes from
// `@o2d/rich-text-editor`'s compiled output, which doesn't re-trigger the
// augmentation for a downstream consumer. `textbox-editor.controller.ts`
// calls `chain.setTextAlign(...)` and needs this augmentation in scope.
import '@tiptap/extension-text-align';

/**
 * Per-paragraph horizontal alignment (Word-like): stores `text-align` as an
 * attribute on each block node rather than one textbox-wide property, so the
 * alignment follows the block the caret sits in. Shared by BOTH extension lists
 * so edit- and view-mode agree byte-for-byte.
 *
 * `textAlignDefault: null` marks a block the user never aligned. TextAlign's
 * `renderHTML` emits an inline `text-align` for any TRUTHY attribute value and
 * skips it only for a falsy one — so a `null` default keeps an untouched block
 * attribute-less, letting it INHERIT the textbox-wide default the builder writes
 * onto the wrapper (from `obj.hAlign`), while every REAL choice (incl. `left`)
 * still renders an explicit rule that overrides the wrapper. Existing textboxes
 * therefore keep their look with no data migration.
 *
 * Do NOT use a non-null sentinel (this was `'justify'` until 2026-07-16): the
 * default value is what `renderHTML` receives, and it compares against falsiness
 * — NOT against `defaultAlignment` — so any truthy sentinel is written verbatim
 * into every untouched block. `'justify'` really did justify the text.
 */
const TEXT_ALIGN_DEFAULT = null;

// Consolidated deferred-format marks: piggy-back on TextStyle by adding the
// `fontWeight`, `textTransform`, and `verticalScript` global attributes in a
// single extension. Mirrors the FontSize pattern so per-run weight, casing,
// and super/subscript survive edit/view round-trips (PRD-RTE-TIPTAP-MARKS §TR1).
//
// `verticalScript` is special: its CSS representation needs two declarations
// (`vertical-align` plus a shrunk `font-size`) so super/subscript runs render
// at the conventional smaller size.
export const DeferredFormatsExt = Extension.create({
  name: 'deferredFormats',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontWeight: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontWeight?.replace(/['"]+/g, '') || null,
            renderHTML: (attributes: { fontWeight?: string | null }) => {
              if (!attributes.fontWeight) {
                return {};
              }
              return { style: `font-weight: ${attributes.fontWeight}` };
            },
          },
          textTransform: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.textTransform?.replace(/['"]+/g, '') || null,
            renderHTML: (attributes: { textTransform?: string | null }) => {
              if (!attributes.textTransform) {
                return {};
              }
              return { style: `text-transform: ${attributes.textTransform}` };
            },
          },
          verticalScript: {
            default: null,
            parseHTML: (element: HTMLElement) => {
              const align = element.style.verticalAlign?.replace(/['"]+/g, '');
              return align === 'super' || align === 'sub' ? align : null;
            },
            renderHTML: (attributes: { verticalScript?: string | null }) => {
              const value = attributes.verticalScript;
              if (value === null || value === undefined || value === 'none') {
                return {};
              }
              return { style: `vertical-align: ${value}; font-size: 0.72em` };
            },
          },
        },
      },
    ];
  },
});

/**
 * Canonical TipTap extension list for the textbox editor and view-mode
 * renderer. Both edit-mode (`Editor` mount) and view-mode (`generateHTML`)
 * MUST use this same list to guarantee identical DOM output.
 */
/**
 * Anke, 2026-07-19 — link handling.
 *
 * StarterKit bundles TipTap's `Link`, whose defaults autolink typed URLs AND
 * open them when the TEXT is clicked. The text-click is switched off here:
 * the link is reachable only through the small icon rendered after the URL
 * (`textbox-html-builder.ts`), so clicking a URL inside a textbox never
 * navigates away by accident — and, since the icon is drawn into the rendered
 * bitmap, it works with the textbox merely sitting on the board.
 *
 * Detection (`autolink`) stays ON — that is the part that should keep working.
 *
 * `protocols` is deliberately NOT widened. TipTap's own `isAllowedUri` blocks
 * `javascript:` and friends by default; the opening side validates the scheme a
 * SECOND time before calling `window.open` (see `openLinkUrl`), because pasted
 * HTML can carry an `<a href>` the autolinker never saw.
 */
const LINK_OPTIONS = {
  openOnClick: false,
  autolink: true,
  defaultProtocol: 'https',
} as const;

// `trailingNode: false` — Bugreport 2026-08-15 (Anke): draw a textbox, type
// "hallo", select all, click numbered list, select all again → a phantom
// "line 2" appears. StarterKit's `trailingNode` extension auto-appends an
// empty paragraph whenever a doc would otherwise end on any top-level node
// OTHER than the schema's `defaultType` (`paragraph`) — NOT lists only.
// Reported/reproduced via a list, but the same TipTap extension fires
// identically for a doc ending on a `heading`, `blockquote`, `codeBlock`,
// or `horizontalRule` (all reachable here via TipTap's standard input
// rules — `#`, `>`, ``` ``` ```, `---` — none of which are disabled in
// this config; tables are the one exception, since tables are off below).
// Its own reasoning: a full-document rich-text editor wants somewhere to
// click below a table/list to keep typing. A textbox is not a document — it
// is a tight, minimal shape — and this was already a known, actively
// worked-around artifact in TWO places before this fix
// (`deriveAdvisoryListType` in text-document-migration.ts,
// `withoutTrailingArtifacts` in document-format-state.ts, both explicitly
// call it "an editor artifact, not something the user made"). Those two
// filters only hid the symptom from DERIVED state (advisory listType,
// selection-mark reads) — the raw document and the live editor DOM still
// carried the extra empty paragraph, which is exactly what surfaced as the
// visible "line 2". Disabling it at the source removes the artifact instead
// of continuing to filter it out downstream in more places. Note: this only
// prevents the artifact from being created going forward — it is not a
// migration, so a document saved before this fix that already has the
// phantom paragraph baked into its JSON keeps it (the existing downstream
// filters remain load-bearing for that case, not just a transitional
// stopgap).
export const TEXTBOX_EXTENSIONS = createRichTextExtensions({
  undoRedo: false,
  link: LINK_OPTIONS,
  trailingNode: false,
  textAlignDefault: TEXT_ALIGN_DEFAULT,
  // The textbox represents super/subscript via `DeferredFormatsExt`'s own
  // `verticalScript` attribute (a CSS-trick emulation, see its doc comment)
  // instead of tiptap's real Superscript/Subscript marks — registering both
  // would give two independent, unsynced ways to express the same thing.
  superscriptSubscript: false,
  // Highlight/Image/Table have no textbox toolbar wiring and no fit for a
  // free-floating textbox (tables especially — see the `trailingNode`
  // comment above for the prior art on why tables are excluded here).
  highlight: false,
  image: false,
  tables: false,
  // Block ids are an open2done document concern (edit commands address blocks
  // by id). A textbox's stored JSON must stay id-free, so the textbox opts out.
  blockIds: false,
  // Nested checklists register `Tab` for indenting; the textbox editor handles
  // `Tab` itself (`textbox-editor.controller.ts`), so the textbox keeps flat
  // checklist items.
  nestedTaskItems: false,
  extras: [DeferredFormatsExt],
});

/**
 * 2026-08-15 (Anke, "clean rewrite" bugreport) — the edit overlay used to
 * mount a SEPARATE extension list (`EDIT_TEXTBOX_EXTENSIONS`) whose
 * `FontSize` variant deliberately suppressed the mark's own `font-size` (to
 * avoid double-applying a zoom-scaled value on top of it). That's gone: the
 * edit overlay now renders its content at NATIVE world-unit sizes — same as
 * the view path — and a single `transform: scale(viewport.scale)` on the
 * editor's content wrapper (`textbox-editor.controller.ts`,
 * `#applyContentTransform`) handles the zoom for every descendant at once,
 * natively, via the browser's own cascade. There is no more reason for edit
 * and view mode to use different marks, so they now share this ONE list —
 * they render byte-for-byte identical DOM for identical content, which is
 * the whole point.
 */
export const EDIT_TEXTBOX_EXTENSIONS = TEXTBOX_EXTENSIONS;
